<template>
  <div v-if="item" class="gmv" :class="{ audio: !item.isVideo }" :style="{ bottom: np && np.id ? '84px' : '16px' }">
    <div class="gmv-pic" @click="toggle">
      <video ref="v" :src="item.src" :poster="item.poster || ''" playsinline autoplay
        @loadedmetadata="onLoaded" @timeupdate="onTime" @play="playing = true" @pause="onPause" @ended="onEnded"></video>
      <div v-if="!item.isVideo" class="gmv-note">♪</div>
      <div class="gmv-progress"><div :style="{ width: pct + '%' }"></div></div>
    </div>
    <div class="gmv-row">
      <div class="gmv-info" @click="expand">
        <div class="gmv-title">{{ item.title }}</div>
        <div class="gmv-sub">{{ item.sub }}</div>
      </div>
      <button class="gmv-btn" :aria-label="playing ? 'Pause' : 'Play'" @click="toggle">{{ playing ? "❚❚" : "▶" }}</button>
      <button v-if="pipOk && item.isVideo" class="gmv-btn" aria-label="Floating window" @click="pip">⧉</button>
      <button class="gmv-btn" aria-label="Open" @click="expand">⤢</button>
      <button class="gmv-btn gmv-close" aria-label="Close" @click="close">✕</button>
    </div>
  </div>
</template>

<script>
import miniVideo from "@/services/miniVideoStore";
import playerStore from "@/services/playerStore";

// The services are loaded on demand (dynamic import), so this always-mounted component never takes part in an import cycle.
const svc = {
  mv: () => import("@/services/musicVideoService"),
  pod: () => import("@/services/podcastService"),
};
const pipSupported = () => typeof document !== "undefined" && !!document.pictureInPictureEnabled;

export default {
  name: "GlobalMiniVideo",
  data() { return { playing: false, pct: 0 }; },
  computed: {
    item() { return miniVideo.state.item; },
    np() { return playerStore.state.nowPlaying; },
    pipOk() { return pipSupported(); },
  },
  watch: {
    item(now, was) { if (!was || !now || was.id !== now.id) { this._resumed = false; this._acc = 0; this._lastT = 0; this.pct = 0; this.playing = false; } },
    // Music started: the mini video steps aside.
    "np.id"(id) { if (id && this.$refs.v && !this.$refs.v.paused) this.$refs.v.pause(); },
  },
  methods: {
    onLoaded() {
      const v = this.$refs.v; if (!v || this._resumed || !this.item) return; this._resumed = true;
      if (this.item.time > 1 && this.item.time < (v.duration || Infinity) - 2) v.currentTime = this.item.time;
      v.play().catch(() => {});
    },
    onTime() {
      const v = this.$refs.v; if (!v || !this.item) return;
      const ct = v.currentTime, dt = ct - (this._lastT || ct);
      if (!v.paused && dt > 0 && dt <= 1.5) { this._acc = (this._acc || 0) + dt; if (this._acc >= 60) this.flush(); }
      this._lastT = ct;
      this.pct = v.duration ? (ct / v.duration) * 100 : 0;
      const now = Date.now();
      if (!this._lastSave || now - this._lastSave > 5000) { this._lastSave = now; this.save(); }
    },
    save() {
      const v = this.$refs.v; const it = this.item; if (!v || !it || !v.currentTime) return;
      const t = v.currentTime, d = v.duration || 0;
      if (it.kind === "video") svc.mv().then((MV) => MV.saveProgress(it.id, t, d)).catch(() => {});
      else if (it.ref) svc.pod().then((P) => P.saveProgress(it.ref.podcast, it.ref.series, it.ref.episode, t, d)).catch(() => {});
    },
    flush() {
      const it = this.item; const sec = Math.round(this._acc || 0);
      if (!it || !it.isMember || sec < 1) { if (it && !it.isMember) this._acc = 0; return; }
      this._acc -= sec;
      if (it.kind === "video") svc.mv().then((MV) => MV.reportWatching(it.id, sec)).catch(() => {});
      else if (it.ref) svc.pod().then((P) => P.reportPodcastListening(it.ref.podcast.id, sec)).catch(() => {});
    },
    onPause() { this.playing = false; this.save(); this.flush(); },
    onEnded() {
      const v = this.$refs.v; const it = this.item; if (!v || !it) return;
      const d = v.duration || 1;
      if (it.kind === "video") svc.mv().then((MV) => MV.saveProgress(it.id, d, d)).catch(() => {});
      else if (it.ref) svc.pod().then((P) => P.saveProgress(it.ref.podcast, it.ref.series, it.ref.episode, d, d)).catch(() => {});
    },
    toggle() { const v = this.$refs.v; if (!v) return; if (v.paused) v.play().catch(() => {}); else v.pause(); },
    pip() {
      const v = this.$refs.v; if (!v || !pipSupported()) return;
      if (document.pictureInPictureElement === v) document.exitPictureInPicture().catch(() => {});
      else v.requestPictureInPicture().catch(() => {});
    },
    expand() {
      const route = this.item.route;
      this.save(); this.flush();
      miniVideo.close();
      if (route && this.$route.fullPath !== route) this.$router.push(route);
    },
    close() { this.save(); this.flush(); miniVideo.close(); },
  },
  beforeDestroy() { this.save(); this.flush(); },
};
</script>

<style scoped>
.gmv { position: fixed; right: 16px; width: 320px; z-index: 9998; background: #14141c; border: 1px solid #2a2a38; border-radius: 12px; overflow: hidden; color: #fff; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }
.gmv-pic { position: relative; aspect-ratio: 16 / 9; background: #000; cursor: pointer; }
.gmv.audio .gmv-pic { aspect-ratio: auto; height: 0; overflow: hidden; }
.gmv-pic video { width: 100%; height: 100%; object-fit: contain; display: block; }
.gmv-note { display: none; }
.gmv-progress { position: absolute; left: 0; right: 0; bottom: 0; height: 3px; background: rgba(255,255,255,0.15); }
.gmv-progress > div { height: 100%; background: #d48c1c; }
.gmv-row { display: flex; align-items: center; gap: 4px; padding: 8px 8px 8px 12px; }
.gmv-info { flex: 1; min-width: 0; cursor: pointer; }
.gmv-title { font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gmv-sub { font-size: 11px; color: #9a9ab0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gmv-btn { background: none; border: none; color: #fff; font-size: 15px; cursor: pointer; padding: 6px 8px; }
.gmv-close { color: #9a9ab0; }
@media (max-width: 600px) { .gmv { left: 8px; right: 8px; width: auto; } }
</style>